import type { Doc } from "building-types/doc";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import type { LumberRail } from "building-types/framing/lumberTypes";
import type { TargetedOpeningSettings } from "building-types/openings/opening";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import { RoofWallFramingType } from "building-types/roofs/roofWallFramingTypes";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import { isOpeningHeaderEnabled } from "building-types/settings/openingHeaderAssemblySettingsTools";
import { RoofSide } from "building-types/settings/roofTypes";
import type { WallFramingSettings } from "building-types/settings/wallFramingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import {
  DEFAULT_STUD_DISTANCE,
  MIN_FRAMING_STUD_LENGTH,
  type Framing,
  type WallFraming,
  WallFramingType,
} from "building-types/walls/wallFramingTypes";
import type { WallGap } from "building-types/walls/wallGap";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import { getLumberProfile, lumberRailNew } from "doc/utils/lumberTools";
import { MULTIGABLE_BSP_EPSILON, GABLE_END_FACE_OFFSET } from "doc/utils/roofTools";
import type { Face } from "geom/brep";
import { faceCopy, faceMoveAlongNormal } from "geom/brep";
import { buildBSPNode, buildEdgePolygon, rayIntersectionBSP } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail3 } from "geom/bspClipping";
import type { NodeBSP, PolygonBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { joinPolyLine3Rails } from "geom/join";
import { offsetPolyLine2Variable } from "geom/offset";
import { planeNew, planeNewXY, planeNormal, planeProjectPolyLine3, polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2EdgeCount, polyLine2EdgeNormal } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import {
  polyLine3ByArray,
  polyLine3ByTwoPoints,
  polyLine3Edge,
  polyLine3EdgeCount,
  polyLine3EdgeEnd,
  polyLine3EdgeSlopeInZ,
  polyLine3EdgeStart,
  polyLine3EdgeTangent,
  polyLine3ExtendBothEnds,
  polyLine3ExtendEnd,
  polyLine3ExtendStart,
  polyLine3Length,
  polyLine3Reverse,
  polyLine3ReverseCopy,
  polyLine3StartPoint,
} from "geom/polyLine3";
import { rangeInclusiveByStep } from "geom/range";
import { vec3Cross } from "geom/vec3";
import { getWallGaps } from "../elements/walls/solidWallCrossSectionUtils";
import { wallFramingNew } from "../elements/walls/wallFraming";
import { wallSideNew } from "../elements/walls/wallSideBasics";
import { getRoofSideWallFramingType } from "./roofs/roofEdges";
import { addWallFramingMember, makeRoughOpening } from "./walls/wallFramingUtils";

interface MakeGableEndWallFramingOptions {
  readonly roofs: GeneratedRoof[];
  readonly wallOverlapBSPs: NodeBSP[];
  readonly wallFramingSettings: WallFramingSettings;
  readonly wallSettings: WallSettings;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly headerFramingSettings: HeaderFramingSettings;
}

export function makeGableEndWallFraming({
  roofs,
  wallOverlapBSPs,
  wallFramingSettings,
  wallSettings,
  targetedOpeningSettings,
  framingComponents,
  headerAssemblies,
  headerFramingSettings,
}: MakeGableEndWallFramingOptions): WallFraming[] {
  const wallFraming: WallFraming[] = [];

  for (const roof of roofs) {
    const fromGableEndParams: FromGableEndParams = {
      wallFraming,
      wallFramingSettings,
      wallOverlapBSPs,
      roof,
      wallSettings,
      targetedOpeningSettings,
      framingComponents,
      headerAssemblies,
      headerFramingSettings,
    };

    frameGableEnd({ ...fromGableEndParams, isEndSide: false });
    frameGableEnd({ ...fromGableEndParams, isEndSide: true });
  }

  return wallFraming;
}

function offsetEdges(profile: PolyLine2, bottomOffset: number, topOffset: number): PolyLine2 {
  const edgeCount = polyLine2EdgeCount(profile);
  const offsets: number[] = new Array<number>(edgeCount).fill(0);
  for (let i = 0; i < edgeCount; i++) {
    const [, ny] = polyLine2EdgeNormal(profile, i, true);

    if (ny > DIST_EPSILON) {
      offsets[i] = -topOffset;
    } else if (ny < -DIST_EPSILON) {
      offsets[i] = -bottomOffset;
    }
  }

  return offsetPolyLine2Variable(profile, offsets);
}

function buildTopEdgesBSP(face: Face, topEdgeOffset: number): NodeBSP | null {
  const { plane, loops } = face;

  if (loops.length < 1) {
    return null;
  }

  const [planeNormalX, planeNormalY, planeNormalZ] = planeNormal(plane);
  const profile2 = offsetEdges(loops[0], 0, topEdgeOffset);
  const profile3 = polyLine2OnPlane(plane, profile2);

  const polygons: PolygonBSP[] = [];
  const edgeCount = profile3 ? polyLine3EdgeCount(profile3) : 0;
  for (let i = 0; i < edgeCount; i++) {
    const [, ny] = polyLine2EdgeNormal(profile2, i, true);

    if (ny < DIST_EPSILON) {
      continue;
    }

    const [x0, y0, z0] = polyLine3EdgeStart(profile3, i);
    const [x1, y1, z1] = polyLine3EdgeEnd(profile3, i);

    const polygon = buildEdgePolygon(x0, y0, z0, x1, y1, z1, -planeNormalX, -planeNormalY, -planeNormalZ);

    if (polygon) {
      polygons.push(polygon);
    }
  }

  return buildBSPNode({ polygons, epsilon: MULTIGABLE_BSP_EPSILON });
}

interface FromGableEndParams {
  wallFraming: WallFraming[];
  wallFramingSettings: WallFramingSettings;
  roof: GeneratedRoof;
  wallSettings: WallSettings;
  wallOverlapBSPs: NodeBSP[];
  isEndSide?: boolean;
  targetedOpeningSettings: TargetedOpeningSettings;
  framingComponents: Doc["framingComponents"];
  headerAssemblies: Doc["headerAssemblies"];
  headerFramingSettings: HeaderFramingSettings;
}

function frameGableEnd({
  roof,
  wallFraming,
  wallFramingSettings,
  wallOverlapBSPs,
  wallSettings,
  isEndSide,
  targetedOpeningSettings,
  framingComponents,
  headerAssemblies,
  headerFramingSettings,
}: FromGableEndParams) {
  const { roofVolume, startWindows, endWindows, wallFaces, rails } = roof;
  const { startFaces, endFaces, baseHeight, peakHeight, edges } = roofVolume;

  const { face: startFrontFace } = startFaces;
  const { face: endFrontFace } = endFaces;

  const { exteriorWallSubstrateThickness } = wallSettings;

  const roofSide = isEndSide ? RoofSide.END : RoofSide.START;
  const framingType = getRoofSideWallFramingType(edges, roofSide);
  let frontFace = isEndSide ? endFrontFace : startFrontFace;

  // if there is no front face, or no framing, then we're done.
  if (!frontFace || frontFace.loops.length < 1 || framingType === RoofWallFramingType.NONE) {
    return;
  }

  // helper function
  // assumes base edge is horizontal
  const getBaseEdge = (wallProfile: PolyLine2): PolyLine2 | undefined => {
    const edgeCount = wallProfile ? polyLine3EdgeCount(wallProfile) : 0;
    let baseEdge: PolyLine2 | undefined = undefined;
    let zMax = Number.POSITIVE_INFINITY;
    for (let i = 0; i < edgeCount; i++) {
      const edge = polyLine3Edge(wallProfile, i);
      const averageZ = (edge[2] + edge[5]) / 2;
      if (averageZ < zMax) {
        zMax = averageZ;
        baseEdge = edge;
      }
    }
    return baseEdge;
  };

  // prepare the wall framing settings
  const { studDistance = DEFAULT_STUD_DISTANCE } = wallFramingSettings;

  const { startWallFaces, endWallFaces } = wallFaces;
  const framingThickness = isEndSide ? startWallFaces.thickness : endWallFaces.thickness;

  const plateLumberProfile = getLumberProfile(framingThickness, true);
  const studLumberProfile = getLumberProfile(framingThickness, false);

  const { height: plateThickness, width: plateWidth } = plateLumberProfile;
  const { height: studThickness } = studLumberProfile;

  const bottomPlateCount = 1;
  const topPlateCount = 1;
  const totalBottomPlateHeight = bottomPlateCount * plateThickness;
  const totalTopPlateHeight = topPlateCount * plateThickness;

  // move the front face back to align with the wall framing below
  frontFace = faceMoveAlongNormal(faceCopy(frontFace), -exteriorWallSubstrateThickness - plateWidth);

  const { plane, loops } = frontFace;
  const { uX: faceUX, uY: faceUY, uZ: faceUZ } = plane;

  // build the top edges BSP
  const topEdgesBSP = buildTopEdgesBSP(frontFace, -totalTopPlateHeight - GABLE_ROOF_THICKNESS);

  // basic stud alignment
  const { leftRail, rightRail, startRail, endRail } = rails;
  const frontLength = polyLine3Length(isEndSide ? endRail : startRail);
  const sideLength = Math.max(polyLine3Length(leftRail), polyLine3Length(rightRail));
  const extraBaseExtension = sideLength > frontLength ? -plateWidth : 0;

  // prepare the base edge (bottom plate)
  const [planeNormalX, planeNormalY, planeNormalZ] = planeNormal(plane);
  const wallProfile = polyLine2OnPlane(plane, offsetEdges(loops[0], GABLE_END_FACE_OFFSET, -GABLE_ROOF_THICKNESS));
  polyLine3Reverse(wallProfile);
  const baseEdgeExtension = -exteriorWallSubstrateThickness + extraBaseExtension;
  const baseEdgeInitial = getBaseEdge(wallProfile);

  assertIsNotNullOrUndefined(baseEdgeInitial);
  const baseEdgeInitialExtended = polyLine3ExtendBothEnds(baseEdgeInitial, baseEdgeExtension, baseEdgeExtension);
  const baseEdge = !isEndSide ? baseEdgeInitialExtended : polyLine3ReverseCopy(baseEdgeInitialExtended);
  const baseEdge2 = planeProjectPolyLine3(planeNewXY(), baseEdge);
  const baseRailLength = polyLine3Length(baseEdge);
  const [baseEdgeStartX, baseEdgeStartY, baseEdgeStartZ] = polyLine3StartPoint(baseEdge);
  const [dirX, dirY] = polyLine3EdgeTangent(baseEdge, 0);

  const framing: Framing[] = [];

  // preare clipping options
  const clipOptions: ClipRailContext = {
    clippingNodes: wallOverlapBSPs.map((x) => clippingNodeNew(x)),
    epsilon: MULTIGABLE_BSP_EPSILON,
    coplanarsToFront: true,
  };

  const addLumber = (rails: LumberRail[], lumberRail: LumberRail) => {
    const { rail } = lumberRail;

    const subRails = clipRail3({
      rail,
      options: clipOptions,
    });

    for (const subRail of subRails) {
      const lumberSubRail = {
        ...lumberRail,
        rail: subRail,
      };

      rails.push(lumberSubRail);
    }

    return true;
  };

  const addPlate = (
    rails: LumberRail[],
    rail: PolyLine3,
    nX: number,
    nY: number,
    nZ: number,
    clipPlane?: Plane | null,
  ) => {
    return addLumber(rails, lumberRailNew(rail, nX, nY, nZ, clipPlane ? [clipPlane] : []));
  };

  const addBottomPlate = (rails: LumberRail[]) => {
    return addPlate(rails, baseEdgeInitialExtended, 0, 0, 1);
  };

  const addTopPlates = (rails: LumberRail[]) => {
    const topSegments: PolyLine3[] = [];
    const edgeCount = wallProfile ? polyLine3EdgeCount(wallProfile) : 0;
    for (let i = 0; i < edgeCount; i++) {
      const edge = polyLine3Edge(wallProfile, i);
      const [tx, ty, tz] = polyLine3EdgeTangent(edge, 0);
      const [, , nZ] = vec3Cross(planeNormalX, planeNormalY, planeNormalZ, tx, ty, tz);
      // ignore vertical edges and the base edge
      if (nZ < -DIST_EPSILON) {
        topSegments.push(edge);
      }
    }

    const joinedTopRails = joinPolyLine3Rails({
      segments: topSegments,
      simplify: true,
    });

    for (let i = 0, l = joinedTopRails.length; i < l; i++) {
      const topRail = joinedTopRails[i];
      if (!topRail) {
        continue;
      }
      const edgeCount = polyLine3EdgeCount(topRail);
      for (let j = 0; j < edgeCount; j++) {
        const edge = polyLine3Edge(topRail, j);
        const [tx, ty, tz] = polyLine3EdgeTangent(edge, 0);
        const [nX, nY, nZ] = vec3Cross(planeNormalX, planeNormalY, planeNormalZ, tx, ty, tz);
        let clipPlane: Plane | null = null;

        if (i === 0 && j === 0) {
          const [x, y, z] = polyLine3EdgeStart(edge, j);
          const slope = Math.abs(polyLine3EdgeSlopeInZ(edge, 0));

          polyLine3ExtendStart(edge, plateThickness * slope);

          clipPlane = planeNew(
            x + faceUX * baseEdgeExtension,
            y + faceUY * baseEdgeExtension,
            z + faceUZ * baseEdgeExtension,
            -planeNormalX,
            -planeNormalY,
            -planeNormalZ,
            0,
            0,
            1,
          );
        }

        if (i === l - 1 && j === edgeCount - 1) {
          const [x, y, z] = polyLine3EdgeEnd(edge, j);
          const slope = Math.abs(polyLine3EdgeSlopeInZ(edge, 0));

          polyLine3ExtendEnd(edge, plateThickness * slope);

          clipPlane = planeNew(
            x - faceUX * baseEdgeExtension,
            y - faceUY * baseEdgeExtension,
            z - faceUZ * baseEdgeExtension,
            planeNormalX,
            planeNormalY,
            planeNormalZ,
            0,
            0,
            1,
          );
        }

        addPlate(rails, edge, nX, nY, nZ, clipPlane);
      }
    }

    return true;
  };

  const addSill = (rails: LumberRail[], startT: number, endT: number, elevation: number) => {
    const sillStartX = baseEdgeStartX + dirX * startT;
    const sillStartY = baseEdgeStartY + dirY * startT;

    const sillEndX = baseEdgeStartX + dirX * endT;
    const sillEndY = baseEdgeStartY + dirY * endT;

    const sillRail = polyLine3ByTwoPoints(sillStartX, sillStartY, elevation, sillEndX, sillEndY, elevation);
    if (isEndSide) {
      polyLine3Reverse(sillRail);
    }

    return addPlate(rails, sillRail, 0, 0, 1);
  };

  const addHeader = (
    rails: LumberRail[],
    startT: number,
    endT: number,
    elevation: number,
    offset: number,
    railId: string,
  ) => {
    const headerStartX = baseEdgeStartX + dirX * startT + dirY * offset;
    const headerStartY = baseEdgeStartY + dirY * startT - dirX * offset;

    const headerEndX = baseEdgeStartX + dirX * endT + dirY * offset;
    const headerEndY = baseEdgeStartY + dirY * endT - dirX * offset;

    const headerRail = polyLine3ByArray([headerStartX, headerStartY, elevation, headerEndX, headerEndY, elevation]);

    return addLumber(rails, lumberRailNew(headerRail, -dirY, dirX, 0, undefined, undefined, undefined, railId));
  };

  const addStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
    if (top - bottom < MIN_FRAMING_STUD_LENGTH) {
      return false;
    }

    const x1 = baseEdgeStartX + dirX * t;
    const y1 = baseEdgeStartY + dirY * t;

    const x2 = x1 + dirX * studThickness;
    const y2 = y1 + dirY * studThickness;

    // look for intersections with the gable end profile
    const intersection1 = rayIntersectionBSP({
      node: topEdgesBSP,
      coplanarsToFront: false,
      x1,
      y1,
      z1: top,
      x2: x1,
      y2: x1,
      z2: bottom,
    });

    const intersection2 = rayIntersectionBSP({
      node: topEdgesBSP,
      coplanarsToFront: false,
      x1: x2,
      y1: y2,
      z1: top,
      x2,
      y2,
      z2: bottom,
    });

    const entryPoint1 = intersection1?.entryPoint;
    const entryPoint2 = intersection2?.entryPoint;

    const studLength1 = entryPoint1 ? entryPoint1.z - bottom : top - bottom;
    const studLength2 = entryPoint2 ? entryPoint2.z - bottom : top - bottom;

    const studLength = Math.max(studLength1, studLength2);

    const rail = polyLine3ByTwoPoints(x1, y1, bottom + studLength, x1, y1, bottom);
    if (isEndSide) {
      polyLine3Reverse(rail);
    }
    return addLumber(rails, lumberRailNew(rail, dirX, dirY, 0));
  };

  const addStandardStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
    return addStud(rails, t, bottom, top);
  };

  const addKingStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
    return addStud(rails, t, bottom, top);
  };

  const addJackStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
    return addStud(rails, t, bottom, top);
  };

  const addBottomCrippleStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
    return addStud(rails, t, bottom, top);
  };

  const addTopCrippleStud = (rails: LumberRail[], t: number, bottom: number, top: number) => {
    return addStud(rails, t, bottom, top);
  };

  const addStuds = () => {
    const steps = rangeInclusiveByStep(0, baseRailLength, studDistance);
    const lastStudT = baseRailLength - studThickness;
    const studBottom = baseEdgeStartZ + totalBottomPlateHeight;
    const studTop = peakHeight;

    const windows = isEndSide ? endWindows : startWindows;
    const gaps: WallGap[] = getWallGaps({
      leftEdge: baseEdge2,
      rightEdge: baseEdge2,
      leftSide: wallSideNew({
        windows: Object.values(windows),
      }),
      rightSide: wallSideNew(),
      isLeftSide: false,
      mergeGaps: true,
    });

    const roughOpenings = gaps.map((gap) => {
      return makeRoughOpening({
        openings: Object.values(windows),
        gap,
        studThickness,
        wallStripBottom: baseHeight,
        levelFloorElevation: baseHeight,
        isGable: true,
        targetedOpeningSettings,
        framingComponents,
        headerAssemblies,
        headerFramingSettings,
      });
    });

    const openingCount = roughOpenings.length;

    let prevStudEndT = 0;
    let currentOpeningId = 0;
    for (let j = 0, lj = steps.length; j < lj; j++) {
      const t = steps[j];
      const startT = t;
      const endT = t + studThickness;

      if (endT >= lastStudT) {
        continue;
      }

      while (currentOpeningId < openingCount && roughOpenings[currentOpeningId].extendedEnd < startT) {
        currentOpeningId++;
      }

      let isGap = false;

      if (currentOpeningId < openingCount) {
        const { extendedStart, extendedEnd } = roughOpenings[currentOpeningId];

        isGap = (extendedStart <= startT && startT <= extendedEnd) || (extendedStart <= endT && endT <= extendedEnd);
      }

      if (isGap) {
        const {
          start,
          end,
          bottom,
          top,
          sillPlateCount,
          hasCrippleStudEnds,
          hasCrippleStudsBelowOpening,
          hasCrippleStudsAboveOpening,
          hasTopHeaderSillPlate,
          headerAssemblyId,
          headerProfile,
          numberOfJackStuds,
        } = roughOpenings[currentOpeningId];
        const { width: headerHeight } = headerProfile;
        const jackStudOffset = numberOfJackStuds * studThickness;
        const headerSillStackHeight = hasTopHeaderSillPlate ? plateThickness : 0;
        const headerEnabled = isOpeningHeaderEnabled(headerAssemblyId);
        const headerStackHeight = headerEnabled ? headerHeight : 0;
        const topCrippleBottom = top + headerSillStackHeight + headerStackHeight;
        const crippleStudEndOffset = hasCrippleStudEnds ? studThickness : 0;
        const minBottomT = start + crippleStudEndOffset;
        const maxBottomT = end - crippleStudEndOffset;

        const minTopT = start + jackStudOffset;
        const maxTopT = end - jackStudOffset;

        if (hasCrippleStudsBelowOpening && bottom > studBottom && startT >= minBottomT && endT <= maxBottomT) {
          addWallFramingMember(
            framing,
            WallFramingType.BOTTOM_CRIPPLE_STUD,
            studLumberProfile,
            (bottomCrippleStudRails) =>
              addBottomCrippleStud(bottomCrippleStudRails, t, studBottom, bottom - sillPlateCount * plateThickness),
          );
        }

        if (hasCrippleStudsAboveOpening && startT >= minTopT && endT <= maxTopT) {
          addWallFramingMember(framing, WallFramingType.TOP_CRIPPLE_STUD, studLumberProfile, (topCrippleStudRails) =>
            addTopCrippleStud(topCrippleStudRails, t, topCrippleBottom, studTop),
          );
        }

        const nextEndT = end + jackStudOffset + studThickness;

        if (nextEndT !== prevStudEndT) {
          prevStudEndT = nextEndT;
        }
      } else {
        addWallFramingMember(framing, WallFramingType.STANDARD_STUD, studLumberProfile, (studRails) =>
          addStandardStud(studRails, t, studBottom, studTop),
        );
        prevStudEndT = endT;
      }
    }

    // openings
    for (let j = 0; j < openingCount; j++) {
      const roughOpening = roughOpenings[j];
      const {
        start,
        end,
        bottom,
        top,
        sillPlateCount,
        headerType,
        numberOfKingStuds,
        hasCrippleStudEnds,
        hasTopHeaderSillPlate,
        headerAssemblyId,
        headerProfile,
        headerAssemblyName,
        headerCount,
        numberOfJackStuds,
        adjacentRooms,
        openingHandle,
      } = roughOpening;
      const { height: headerThickness } = headerProfile;
      const headerEnabled = isOpeningHeaderEnabled(headerAssemblyId);
      const jackStudOffset = numberOfJackStuds * studThickness;
      const kingStudCount = numberOfKingStuds;
      const headerSillStackHeight = hasTopHeaderSillPlate ? plateThickness : 0;
      const headerElevation = top + headerSillStackHeight;

      // king studs
      for (let k = 0; k < kingStudCount; k++) {
        addWallFramingMember(framing, WallFramingType.KING_STUD, studLumberProfile, (kingStudRails) =>
          addKingStud(kingStudRails, start - (k + 1) * studThickness - jackStudOffset, studBottom, studTop),
        );
        addWallFramingMember(framing, WallFramingType.KING_STUD, studLumberProfile, (kingStudRails) =>
          addKingStud(kingStudRails, end + k * studThickness + jackStudOffset, studBottom, studTop),
        );
      }

      // jack studs
      if (numberOfJackStuds > 0) {
        const jackStudTop = top;
        for (let k = 0; k < numberOfJackStuds; k++) {
          addWallFramingMember(framing, WallFramingType.JACK_STUD, studLumberProfile, (jackStudRails) =>
            addJackStud(jackStudRails, start - jackStudOffset + k * studThickness, studBottom, jackStudTop),
          );

          addWallFramingMember(framing, WallFramingType.JACK_STUD, studLumberProfile, (jackStudRails) =>
            addJackStud(jackStudRails, end + jackStudOffset - (k + 1) * studThickness, studBottom, jackStudTop),
          );
        }
      }

      if (hasTopHeaderSillPlate) {
        addWallFramingMember(framing, WallFramingType.TOP_SILL_PLATE, plateLumberProfile, (headerSillRails) =>
          addSill(headerSillRails, start - jackStudOffset, end + jackStudOffset, top),
        );
      }

      if (headerEnabled) {
        addWallFramingMember(
          framing,
          WallFramingType.HEADER,
          headerProfile,
          (headerRails) => {
            const headerRailGroupId = uid();
            for (let h = 0; h < headerCount; h++) {
              const offset = !isEndSide
                ? plateLumberProfile.width - headerThickness * h
                : -plateLumberProfile.width + headerThickness * (h + 1);
              addHeader(
                headerRails,
                start - jackStudOffset,
                end + jackStudOffset,
                headerElevation,
                offset,
                headerRailGroupId,
              );
            }
            return headerCount > 0;
          },
          {
            adjacentRooms,
            openingHandle,
            source: headerType,
            headerAssemblyName,
          },
        );
      }

      if (bottom > studBottom) {
        const sillElevation = bottom - sillPlateCount * plateThickness;

        // side cripple studs
        if (hasCrippleStudEnds) {
          addWallFramingMember(framing, WallFramingType.BOTTOM_CRIPPLE_STUD, studLumberProfile, (crippleStudRails) =>
            addBottomCrippleStud(crippleStudRails, start, studBottom, sillElevation),
          );
          addWallFramingMember(framing, WallFramingType.BOTTOM_CRIPPLE_STUD, studLumberProfile, (crippleStudRails) =>
            addBottomCrippleStud(crippleStudRails, end - studThickness, studBottom, sillElevation),
          );
        }

        // sill
        for (let s = 0; s < sillPlateCount; s++) {
          addWallFramingMember(framing, WallFramingType.BOTTOM_SILL_PLATE, plateLumberProfile, (sillRails) =>
            addSill(sillRails, start, end, sillElevation + s * plateThickness),
          );
        }
      }
    }

    // end stud
    addWallFramingMember(framing, WallFramingType.STANDARD_STUD, studLumberProfile, (studRails) =>
      addStandardStud(studRails, baseRailLength - studThickness, studBottom, studTop),
    );
  };

  // add the different framing elements
  addWallFramingMember(framing, WallFramingType.BOTTOM_PLATE, plateLumberProfile, (bottomPlateRails) =>
    addBottomPlate(bottomPlateRails),
  );

  addWallFramingMember(framing, WallFramingType.TOP_PLATE, plateLumberProfile, (topPlateRails) =>
    addTopPlates(topPlateRails),
  );
  addStuds();
  wallFraming.push(wallFramingNew(framing, topEdgesBSP));
}
